import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Search, Loader2, ShoppingBag, Check, AlertTriangle, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { ProductDetailModal } from '../restaurant/ProductDetailModal';
import { formatCurrency, cn } from '../../lib/utils';
import type { MenuItem, CartItemOption } from '../../lib/types';
interface PendingItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
interface CabanaAddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  restaurantId: number | string | undefined;
  /** Called after items are successfully added so the parent can refetch */
  onAdded: () => void;
}
/**
 * Cabaña Parrillera — exclusive cashier tool to ADD products to an
 * existing open table order. Mirrors create-order.ts insert shape so
 * the KDS picks up the new items automatically.
 *
 * Safety:
 *  - Only inserts new order_items + order_item_options rows.
 *  - Updates orders.subtotal / orders.total based on the new active
 *    items + the existing delivery_fee (does not touch other columns).
 *  - Does NOT mutate order status, payments, or any item already in
 *    the order. If the order is already paid/closed, the parent
 *    decides whether to expose this modal.
 */
export function CabanaAddItemsModal({
  isOpen,
  onClose,
  orderId,
  restaurantId,
  onAdded
}: CabanaAddItemsModalProps) {
  const {
    categories
  } = useMenuCategories(restaurantId ? String(restaurantId) : undefined);
  const {
    items: menuItems,
    loading: menuLoading
  } = useMenuItems(restaurantId ? String(restaurantId) : undefined);
  const [search, setSearch] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [cart, setCart] = useState<PendingItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset whenever modal closes
  useEffect(() => {
    if (!isOpen) {
      setCart([]);
      setError(null);
      setSearch('');
      setSelectedProduct(null);
      setActiveCategoryId(null);
    }
  }, [isOpen]);
  const activeCategories = useMemo(() => (categories || []).filter((c: any) => c.is_active).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)), [categories]);
  const visibleItems = useMemo(() => {
    let list = (menuItems || []).filter((i: any) => i.is_active !== false);
    if (activeCategoryId) {
      list = list.filter((i: any) => i.category_id === activeCategoryId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i: any) => (i.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [menuItems, activeCategoryId, search]);
  const cartTotal = useMemo(() => cart.reduce((sum, ci) => {
    const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
    return sum + (Number(ci.menuItem.price) + optTotal) * ci.quantity;
  }, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, ci) => s + ci.quantity, 0), [cart]);
  const handleProductPicked = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      menuItem: item,
      quantity,
      options,
      notes
    }]);
    setSelectedProduct(null);
  };
  const updateQty = (lineId: string, delta: number) => {
    setCart(prev => prev.map(ci => ci.id === lineId ? {
      ...ci,
      quantity: Math.max(0, ci.quantity + delta)
    } : ci).filter(ci => ci.quantity > 0));
  };
  const removeLine = (lineId: string) => {
    setCart(prev => prev.filter(ci => ci.id !== lineId));
  };
  const handleConfirm = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la orden.');
      return;
    }
    if (cart.length === 0) {
      setError('Agrega al menos un producto.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 1) Insert each new order_item (mirrors create-order.ts shape)
      for (const ci of cart) {
        const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
        const unitPrice = Number(ci.menuItem.price) || 0;
        const lineTotal = (unitPrice + optTotal) * ci.quantity;
        const {
          data: inserted,
          error: itemErr
        } = await supabase.from('order_items').insert({
          order_id: orderId,
          restaurant_id: Number(restaurantId),
          menu_item_id: ci.menuItem.id,
          item_name_snapshot: ci.menuItem.name,
          unit_price_snapshot: unitPrice,
          quantity: ci.quantity,
          notes: ci.notes || null,
          line_total: lineTotal,
          status: 'pending'
        }).select('id').single();
        if (itemErr) throw itemErr;
        if (ci.options.length > 0 && inserted) {
          const optsPayload = ci.options.map(o => ({
            order_item_id: inserted.id,
            restaurant_id: Number(restaurantId),
            option_group: o.option_group || '',
            option_name: o.option_name || '',
            price_delta: Number(o.price_delta) || 0
          }));
          const {
            error: optErr
          } = await supabase.from('order_item_options').insert(optsPayload);
          if (optErr) {
            console.warn('[CabanaAddItems] options insert non-blocking error:', optErr);
          }
        }
      }
      // 2) Recompute order subtotal/total from all NON-cancelled items
      const {
        data: allItems,
        error: fetchErr
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', orderId);
      if (fetchErr) throw fetchErr;
      const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
      const {
        data: orderRow
      } = await supabase.from('orders').select('delivery_fee, status').eq('id', orderId).maybeSingle();
      const deliveryFee = Number(orderRow?.delivery_fee) || 0;
      const newTotal = newSubtotal + deliveryFee;
      const updatePayload: Record<string, any> = {
        subtotal: newSubtotal,
        total: newTotal,
        updated_at: new Date().toISOString()
      };
      // If the order was already marked 'ready', bump it back to
      // 'preparing' so the KDS shows it again with the new pending items.
      if (orderRow?.status === 'ready') {
        updatePayload.status = 'preparing';
      }
      const {
        error: updErr
      } = await supabase.from('orders').update(updatePayload).eq('id', orderId);
      if (updErr) throw updErr;
      onAdded();
      onClose();
    } catch (e: any) {
      console.error('[CabanaAddItems] add error:', e);
      setError(e?.message || 'No se pudieron agregar los productos.');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-257">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={cn('fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4',
    // While the product detail picker is open, hide this overlay
    // so the picker (Modal.tsx uses z-50) actually receives clicks.
    selectedProduct && 'invisible pointer-events-none')} onClick={onClose} data-id="element-258">
        <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 40,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 28
      }} className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[94vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-259">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-260">
            <div className="flex items-center gap-3" data-id="element-261">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center" data-id="element-262">
                <Plus className="w-5 h-5 text-amber-600" data-id="element-263" />
              </div>
              <div data-id="element-264">
                <h2 className="text-lg font-black text-gray-900 leading-tight" data-id="element-265">
                  Adicionar Productos
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-266">
                  Se agregan a la orden activa y se envían a cocina.
                </p>
              </div>
            </div>
            <button onClick={onClose} disabled={submitting} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40" aria-label="Cerrar" data-id="element-267">
              <X className="w-5 h-5" data-id="element-268" />
            </button>
          </div>

          {/* Search + Categories */}
          <div className="px-5 pt-3 pb-2 shrink-0 space-y-3" data-id="element-269">
            <div className="relative" data-id="element-270">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-271" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar producto…" className="w-full pl-9 pr-3 h-10 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-amber-400" data-id="element-272" />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-id="element-273">
              <button onClick={() => setActiveCategoryId(null)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors', activeCategoryId === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')} data-id="element-274">
                Todos
              </button>
              {activeCategories.map((c: any) => <button key={c.id} onClick={() => setActiveCategoryId(c.id)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors', activeCategoryId === c.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')} data-id="element-275">
                  {c.name}
                </button>)}
            </div>
          </div>

          {/* Body: product grid */}
          <div className="flex-1 overflow-y-auto px-5 pb-3" data-id="element-276">
            {menuLoading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-277">
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-278" />
              </div> : visibleItems.length === 0 ? <div className="text-center py-16 text-gray-500 text-sm" data-id="element-279">
                Ningún producto coincide con la búsqueda.
              </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-280">
                {visibleItems.map((item: any) => <button key={item.id} onClick={() => setSelectedProduct(item)} className="text-left p-3 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/40 active:scale-[0.98] transition-all flex flex-col gap-1" data-id="element-281">
                    <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2" data-id="element-282">
                      {item.name}
                    </p>
                    <p className="text-xs font-black text-amber-600 tabular-nums mt-auto" data-id="element-283">
                      {formatCurrency(item.price)}
                    </p>
                  </button>)}
              </div>}
          </div>

          {/* Pending cart */}
          {cart.length > 0 && <div className="border-t border-gray-100 px-5 py-3 max-h-[28vh] overflow-y-auto shrink-0 bg-gray-50" data-id="element-284">
              <div className="flex items-center gap-2 mb-2" data-id="element-285">
                <ShoppingBag className="w-4 h-4 text-gray-500" data-id="element-286" />
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-287">
                  Por adicionar ({cartCount})
                </p>
              </div>
              <div className="space-y-2" data-id="element-288">
                {cart.map(ci => {
              const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
              const lineTotal = (Number(ci.menuItem.price) + optTotal) * ci.quantity;
              return <div key={ci.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-start gap-2" data-id="element-289">
                      <div className="flex-1 min-w-0" data-id="element-290">
                        <p className="text-sm font-bold text-gray-900 truncate" data-id="element-291">
                          {ci.menuItem.name}
                        </p>
                        {ci.options.length > 0 && <p className="text-[11px] text-gray-500 leading-tight" data-id="element-292">
                            {ci.options.map(o => o.option_name).join(' · ')}
                          </p>}
                        {ci.notes && <p className="text-[11px] text-amber-700 italic" data-id="element-293">
                            "{ci.notes}"
                          </p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0" data-id="element-294">
                        <button onClick={() => updateQty(ci.id, -1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-295">
                          <Minus className="w-3.5 h-3.5" data-id="element-296" />
                        </button>
                        <span className="w-6 text-center font-black text-sm text-gray-900 tabular-nums" data-id="element-297">
                          {ci.quantity}
                        </span>
                        <button onClick={() => updateQty(ci.id, +1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-298">
                          <Plus className="w-3.5 h-3.5" data-id="element-299" />
                        </button>
                        <button onClick={() => removeLine(ci.id)} className="w-7 h-7 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center ml-1" aria-label="Quitar" data-id="element-300">
                          <Trash2 className="w-3.5 h-3.5" data-id="element-301" />
                        </button>
                      </div>
                      <span className="text-sm font-black text-gray-900 tabular-nums shrink-0 w-20 text-right" data-id="element-302">
                        {formatCurrency(lineTotal)}
                      </span>
                    </div>;
            })}
              </div>
            </div>}

          {/* Footer */}
          <div className="border-t border-gray-100 px-5 py-4 bg-white shrink-0" data-id="element-303">
            {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-304">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-305" />
                <span data-id="element-306">{error}</span>
              </div>}
            <div className="flex items-center justify-between mb-3" data-id="element-307">
              <span className="text-sm font-bold text-gray-600" data-id="element-308">
                Total a adicionar
              </span>
              <span className="text-2xl font-black text-amber-600 tabular-nums" data-id="element-309">
                {formatCurrency(cartTotal)}
              </span>
            </div>
            <button onClick={handleConfirm} disabled={submitting || cart.length === 0} className="w-full h-12 rounded-xl font-black text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-310">
              {submitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-311" />
                  Agregando…
                </> : <>
                  <Check className="w-5 h-5" data-id="element-312" />
                  Adicionar a la orden
                </>}
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Product detail picker — reuses existing component */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleProductPicked} data-id="element-313" />}
    </AnimatePresence>;
}